<template>
  <div class="financial-account-detail-page">
    <CommonPageShell
      :title="text.page.title"
      :actions="headerActions"
      @action="onHeaderAction"
    >
      <template #left>
        <div class="financial-account-detail-header-left">
          <AppBreadcrumb
            mode="manual"
            :items="breadcrumbItems"
            context-key="app.breadcrumb.financial-account-detail"
            :context-id="String(financialAccountId || '')"
            :reference="accountReference"
            :labels="{ title: text.page.title, backLabel: text.page.backLabel }"
            :aria-label="text.page.breadcrumbAriaLabel"
            :show-back="true"
            :back-label="text.page.backLabel"
            :fallback-target="{ name: 'financial' }"
          />

          <div class="app-detail-header financial-account-detail-heading-wrap">
            <div class="app-detail-heading financial-account-detail-heading-content">
              <div class="financial-account-detail-heading-main">
                <div class="financial-account-detail-heading-title-row">
                  <h2 class="financial-account-detail-heading-title">{{ accountTitle }}</h2>
                  <span
                    class="app-status-pill app-detail-status-pill"
                    :class="accountActive ? 'app-status-active' : 'app-status-inactive'"
                  >
                    {{ accountStatusLabel }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #content>
        <section class="financial-account-detail-content-wrap">
          <AppSectionTabs
            v-model="activeTab"
            :tabs="tabs"
            :aria-label="text.page.tabsAriaLabel"
          />

          <section v-if="activeTab === 'movements'" class="financial-account-movements-tab">
            <section class="financial-account-toolbar app-filter-toolbar">
              <div
                class="toolbar-main-row app-filter-toolbar-row invoices-toolbar-row"
                :class="{ 'invoices-toolbar-row--open': showFilters }"
              >
                <div class="search-wrap app-filter-search-wrap">
                  <i class="fa fa-search" aria-hidden="true"></i>
                  <input
                    v-model.trim="search"
                    type="text"
                    :placeholder="text.movements.searchPlaceholder"
                    @keyup.enter.prevent
                  />
                </div>

                <div class="toolbar-right app-filter-toolbar-right financial-account-toolbar__right">
                  <AppFilterDateRange
                    v-model="dateRangeValue"
                    class="app-filter-date-inline"
                    label=""
                    :show-input="showDateInput"
                    :presets="text.movements.dateRangePresets"
                    initial-preset-key="this_month"
                    @change="onDateRangeChange"
                  />

                  <multiselect
                    v-model="periodModel"
                    :options="periodOptions"
                    track-by="value"
                    label="label"
                    :multiple="false"
                    :close-on-select="true"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :show-labels="false"
                    :searchable="false"
                    :allow-empty="false"
                    class="financial-toolbar-category-inline app-filter-inline-select app-filter-select app-select app-select--filters"
                  >
                    <template #singleLabel="{ option }">
                      <span class="multiselect__single">{{ option ? option.label : '' }}</span>
                    </template>
                    <template #noResult>
                      <span>{{ text.movements.filters.noOptions }}</span>
                    </template>
                    <template #noOptions>
                      <span>{{ text.movements.filters.noOptions }}</span>
                    </template>
                  </multiselect>

                  <button
                    type="button"
                    class="icon-btn app-filter-icon-btn"
                    :class="{ active: showFilters }"
                    :title="text.movements.filters.toggleButton"
                    @click="toggleFilters"
                  >
                    <i class="fa fa-filter" aria-hidden="true"></i>
                  </button>
                </div>
              </div>

              <transition name="filters-expand" @after-leave="handleFiltersAfterLeave">
                <div v-show="showFilters" class="filters-panel app-filter-panel">
                  <div class="financial-account-filters-grid">
                    <div class="filter-field app-filter-field">
                      <span>{{ text.movements.filters.type.label }}</span>
                      <multiselect
                        v-model="movementTypeModel"
                        :options="movementTypeOptions"
                        track-by="value"
                        label="label"
                        :multiple="false"
                        :close-on-select="true"
                        :clear-on-select="false"
                        :preserve-search="true"
                        :show-labels="false"
                        :searchable="false"
                        :allow-empty="false"
                        class="app-filter-select app-select app-select--filters"
                      >
                        <template #noResult>
                          <span>{{ text.movements.filters.noOptions }}</span>
                        </template>
                        <template #noOptions>
                          <span>{{ text.movements.filters.noOptions }}</span>
                        </template>
                      </multiselect>
                    </div>

                    <div class="filter-field app-filter-field">
                      <span>{{ text.movements.filters.movementType.label }}</span>
                      <multiselect
                        v-model="categoryModel"
                        :options="categoryOptions"
                        track-by="value"
                        label="label"
                        :multiple="false"
                        :close-on-select="true"
                        :clear-on-select="false"
                        :preserve-search="true"
                        :show-labels="false"
                        :searchable="false"
                        :allow-empty="false"
                        class="app-filter-select app-select app-select--filters"
                      >
                        <template #noResult>
                          <span>{{ text.movements.filters.noOptions }}</span>
                        </template>
                        <template #noOptions>
                          <span>{{ text.movements.filters.noOptions }}</span>
                        </template>
                      </multiselect>
                    </div>
                  </div>
                </div>
              </transition>
            </section>

            <div class="app-table-wrap">
              <div class="app-table-scroll">
                <table class="app-data-table">
                  <thead>
                    <tr>
                      <th>{{ text.movements.table.date }}</th>
                      <th>{{ text.movements.table.movementType }}</th>
                      <th>{{ text.movements.table.concept }}</th>
                      <th class="app-text-center financial-col-center">{{ text.movements.table.deposit }}</th>
                      <th class="app-text-center financial-col-center">{{ text.movements.table.withdrawal }}</th>
                      <th class="app-text-center financial-col-center">{{ text.movements.table.balance }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="filteredRows.length === 0">
                      <td colspan="6" class="app-table-empty">{{ text.movements.empty }}</td>
                    </tr>
                    <template v-for="(row, index) in groupedRows" :key="`group-row-${row.id}-${index}`">
                      <tr v-if="row.showMonthDivider" class="financial-account-month-divider-row">
                        <td colspan="6" class="financial-account-month-divider-cell">
                          <span>{{ row.monthLabel }}</span>
                        </td>
                      </tr>

                      <tr>
                      <td>
                        <div class="app-strong">{{ row.date }}</div>
                        <small class="app-muted">{{ row.time ? `${row.time} hrs` : '' }}</small>
                      </td>
                      <td>
                        <span class="financial-category-pill" :class="resolveCategoryClass(resolveMovementKey(row))">{{ resolveMovementLabel(row) }}</span>
                      </td>
                      <td>
                        <div class="app-strong">
                          <template v-if="showPaymentFolioHeader(row)">
                            Pago
                            <router-link
                              v-if="resolvePaymentFolioRoute(row)"
                              :to="resolvePaymentFolioRoute(row)"
                            >
                              {{ row.concept_payment_folio }}
                            </router-link>
                            <span v-else>{{ row.concept_payment_folio }}</span>
                            {{ ` ${row.concept_title || ''}` }}
                            <router-link
                              v-if="resolveConceptSubtitleRoute(row)"
                              :to="resolveConceptSubtitleRoute(row)"
                            >
                              {{ row.concept_subtitle }}
                            </router-link>
                            <span v-else>{{ row.concept_subtitle }}</span>
                          </template>
                          <template v-else>
                            {{ row.concept_title || row.concept }}
                            <template v-if="row.concept_subtitle">
                              <router-link
                                v-if="resolveConceptSubtitleRoute(row)"
                                :to="resolveConceptSubtitleRoute(row)"
                              >
                                {{ row.concept_subtitle }}
                              </router-link>
                              <span v-else>{{ row.concept_subtitle }}</span>
                            </template>
                          </template>
                        </div>

                        <div class="financial-account-concept-detail-row">
                          <small class="app-muted">
                            {{ row.concept_detail_label || 'Concepto:' }}
                            <router-link
                              v-if="resolveConceptDetailRoute(row)"
                              :to="resolveConceptDetailRoute(row)"
                            >
                              {{ row.concept_detail || row.concept || '' }}
                            </router-link>
                            <span v-else>{{ row.concept_detail || row.concept || '' }}</span>
                          </small>
                          <small class="app-muted">{{ row.concept_detail_amount_label || '' }}</small>
                        </div>
                      </td>
                      <td class="app-text-right">
                        <strong class="financial-amount-positive">
                          {{ Number(row.is_income || 0) === 1 ? row.amount_label : '' }}
                        </strong>
                      </td>
                      <td class="app-text-right">
                        <strong class="financial-amount-negative">
                          {{ Number(row.is_income || 0) === 1 ? '' : row.amount_label }}
                        </strong>
                      </td>
                      <td class="app-text-right">
                        <strong>{{ row.balance_label }}</strong>
                      </td>
                    </tr>
                    </template>

                    <tr v-if="filteredRows.length > 0" class="financial-account-movements-total-row">
                      <td colspan="3" class="financial-account-movements-total-label">
                        <strong>{{ text.movements.table.total }}</strong>
                      </td>
                      <td class="app-text-right financial-account-movements-total-cell">
                        <strong class="financial-amount-positive">{{ movementTotals.depositLabel }}</strong>
                      </td>
                      <td class="app-text-right financial-account-movements-total-cell">
                        <strong class="financial-amount-negative">{{ movementTotals.withdrawalLabel }}</strong>
                      </td>
                      <td class="app-text-right financial-account-movements-total-cell"></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <FinancialAccountDetailHistoryTab
            v-if="activeTab === 'history'"
            :financial-account-id="financialAccountId"
          ></FinancialAccountDetailHistoryTab>

          <FinancialAccountDetailSummaryTab
            v-if="activeTab === 'summary'"
            :account="account"
            :financial-account-id="financialAccountId"
            :status-label="accountStatusLabel"
            @edit-section="openEditAccountModal"
            @open-movements="activeTab = 'movements'"
          />
        </section>
      </template>
    </CommonPageShell>

    <FinancialAccountModal
      mode="edit"
      :show="isEditAccountModalOpen"
      :saving="editAccountSaving"
      :initial-data="editAccountData"
      :initial-section-key="editAccountSectionKey"
      @close="closeEditAccountModal"
      @submit="handleEditAccountSubmit"
      @request-disable="handleRequestDisableAccount"
      @request-enable="handleRequestEnableAccount"
      @request-delete="handleRequestDeleteAccount"
    />

    <ConfirmActionModal
      :show="isConfirmActionModalOpen"
      :tone="confirmActionTone"
      :loading="confirmActionLoading || isFinancialAccountSyncRunning"
      :title="confirmActionTitle"
      :subtitle="confirmActionSubtitle"
      :message="confirmActionMessage"
      :confirm-text="confirmActionButton"
      :cancel-text="text.actions.cancel"
      @confirm="confirmPendingAction"
      @cancel="closeConfirmActionModal"
    />

    <ExportFormatModal
      :show="isExportModalOpen"
      :filters="exportFilters"
      :search-term="search"
      :page-customer-ids="currentPageMovementIds"
      :text-catalog="exportText"
      :export-service="exportService"
      :show-include-summary="true"
      :show-include-logo="true"
      :extra-payload="exportExtraPayload"
      file-name-prefix="movimientos_cuenta_financiera"
      @close="closeExportModal"
    />
  </div>
</template>

<script>
import AppBreadcrumb from '@/components/common/AppBreadcrumb.vue';
import AppFilterDateRange from '@/components/common/AppFilterDateRange.vue';
import AppSectionTabs from '@/components/common/AppSectionTabs.vue';
import CommonPageShell from '@/components/common/CommonPageShell.vue';
import ExportFormatModal from '@/components/common/exports/ExportFormatModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import FinancialAccountModal from '@/components/financial/modals/FinancialAccountModal.vue';
import FinancialAccountDetailHistoryTab from '@/components/financial/tabs/FinancialAccountDetailHistoryTab.vue';
import FinancialAccountDetailSummaryTab from '@/components/financial/tabs/FinancialAccountDetailSummaryTab.vue';
import { useDomainText } from '@/composables/text/useDomainText';
import { extractToolbarDateRange, resolveToolbarToggleState, shouldEmitToolbarDateChange } from '@/composables/shared/useFilterToolbarShared';
import financialAccountsService from '@/services/financialAccounts';
import { extractApiErrorMessage, extractApiSuccessMessage, extractPayload } from '@/utils/dataNormalization';
import { formatCurrencyDisplay } from '@/utils/numberFormat';
import { buildCurrentMonthDateRange, formatMonthYearLabel, parsePresentationDate } from '@/utils/datePresentation';
import { buildComparableHaystack, toComparableSearchText } from '@/utils/textValue';

const { catalog: domainText } = useDomainText('financial');

const FINANCIAL_ACCOUNT_SYNC_COOLDOWN_MS = 30 * 60 * 1000;

const buildPeriodValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const buildMonthRangeFromValue = (value) => {
  const raw = String(value || '').trim();
  const [yearText, monthText] = raw.split('-');
  const year = Number(yearText);
  const month = Number(monthText);

  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return null;
  }

  return [new Date(year, month - 1, 1), new Date(year, month, 0)];
};

export default {
  name: 'FinancialAccountDetailPage',
  components: {
    AppBreadcrumb,
    AppFilterDateRange,
    AppSectionTabs,
    CommonPageShell,
    ExportFormatModal,
    ConfirmActionModal,
    FinancialAccountDetailHistoryTab,
    FinancialAccountDetailSummaryTab,
    FinancialAccountModal,
  },
  data() {
    return {
      text: domainText.detailAccount,
      activeTab: 'movements',
      search: '',
      category: 'all',
      movementType: 'all',
      period: '',
      showFilters: false,
      showDateInput: false,
      applyingPeriodSelection: false,
      dateRangeValue: buildCurrentMonthDateRange(),
      rows: [],
      account: null,
      isEditAccountModalOpen: false,
      editAccountSaving: false,
      editAccountData: null,
      editAccountSectionKey: 'general',
      isConfirmActionModalOpen: false,
      confirmActionLoading: false,
      isFinancialAccountSyncRunning: false,
      pendingActionType: '',
      isExportModalOpen: false,
      counts: {
        all: 0,
        income: 0,
        expense: 0,
        transfer: 0,
        init: 0,
        otherincome: 0,
        otherexpense: 0,
      },
    };
  },
  computed: {
    financialAccountId() {
      return Number(this.$route?.params?.financialAccountId || 0);
    },
    tabs() {
      return [
        { key: 'summary', label: this.text.tabs.summary },
        { key: 'movements', label: this.text.tabs.movements },
        { key: 'history', label: this.text.tabs.history },
      ];
    },
    categoryOptions() {
      const labels = this.text.movements.categories;

      return [
        { value: 'all', label: `${labels.all} (${Number(this.counts.all || 0)})` },
        { value: 'income', label: `${labels.income} (${Number(this.counts.income || 0)})` },
        { value: 'expense', label: `${labels.expense} (${Number(this.counts.expense || 0)})` },
        { value: 'transfer', label: `${labels.transfer} (${Number(this.counts.transfer || 0)})` },
        { value: 'init', label: `${labels.init} (${Number(this.counts.init || 0)})` },
        { value: 'otherincome', label: `${labels.otherincome} (${Number(this.counts.otherincome || 0)})` },
        { value: 'otherexpense', label: `${labels.otherexpense} (${Number(this.counts.otherexpense || 0)})` },
      ];
    },
    movementTypeOptions() {
      return [
        { value: 'all', label: this.text.movements.filters.movementType.options.all },
        { value: 'income', label: this.text.movements.filters.movementType.options.income },
        { value: 'expense', label: this.text.movements.filters.movementType.options.expense },
      ];
    },
    periodOptions() {
      const options = [{ value: '', label: this.text.movements.filters.period.none }];
      const today = new Date();

      for (let offset = 0; offset <= 12; offset += 1) {
        const date = new Date(today.getFullYear(), today.getMonth() - offset, 1);
        const label = formatMonthYearLabel(date, { fallback: '' });
        options.push({
          value: buildPeriodValue(date),
          label,
        });
      }

      return options;
    },
    filteredRows() {
      const sourceRows = Array.isArray(this.rows) ? this.rows : [];
      const searchTerm = toComparableSearchText(this.search);

      return sourceRows.filter((row) => {
        const isIncome = Number(row?.is_income || 0) === 1;
        const typeMatches = this.movementType === 'all'
          ? true
          : (this.movementType === 'income' ? isIncome : !isIncome);

        if (!typeMatches) {
          return false;
        }

        if (!searchTerm) {
          return true;
        }

        const movementLabel = this.resolveMovementLabel(row);
        const haystack = buildComparableHaystack([
          row?.date,
          row?.time,
          row?.concept,
          row?.concept_title,
          row?.concept_subtitle,
          row?.concept_payment_folio,
          row?.concept_detail_label,
          row?.concept_detail,
          row?.concept_detail_amount_label,
          row?.reference,
          row?.category,
          row?.category_key,
          movementLabel,
          row?.type_reference,
          row?.amount,
          row?.amount_label,
          row?.balance,
          row?.balance_label,
          row?.currency,
        ]);

        return haystack.includes(searchTerm);
      });
    },
    groupedRows() {
      const rows = Array.isArray(this.filteredRows) ? this.filteredRows : [];
      let lastMonthKey = '';

      return rows.map((row) => {
        const monthMeta = this.resolveMonthMeta(row);
        const showMonthDivider = monthMeta.key !== '' && monthMeta.key !== lastMonthKey;

        if (monthMeta.key !== '') {
          lastMonthKey = monthMeta.key;
        }

        return {
          ...row,
          showMonthDivider,
          monthLabel: monthMeta.label,
        };
      });
    },
    movementTotals() {
      const sourceRows = Array.isArray(this.filteredRows) ? this.filteredRows : [];
      const deposit = sourceRows.reduce((accumulator, row) => {
        if (Number(row?.is_income || 0) !== 1) {
          return accumulator;
        }

        return accumulator + Number(row?.amount || 0);
      }, 0);

      const withdrawal = sourceRows.reduce((accumulator, row) => {
        if (Number(row?.is_income || 0) === 1) {
          return accumulator;
        }

        return accumulator + Number(row?.amount || 0);
      }, 0);

      const currencyCode = String(this.account?.currency || 'MXN').trim().toUpperCase() || 'MXN';

      return {
        deposit,
        withdrawal,
        depositLabel: formatCurrencyDisplay(deposit, { currency: currencyCode }),
        withdrawalLabel: formatCurrencyDisplay(withdrawal, { currency: currencyCode }),
      };
    },
    exportService() {
      return financialAccountsService;
    },
    exportText() {
      return domainText;
    },
    exportFilters() {
      return {
        category: this.category,
        movement_type: this.movementType,
        ...extractToolbarDateRange(this.dateRangeValue, {
          startKey: 'date_from',
          endKey: 'date_to',
        }),
      };
    },
    exportExtraPayload() {
      return {
        financial_account_id: this.financialAccountId,
      };
    },
    currentPageMovementIds() {
      return (Array.isArray(this.filteredRows) ? this.filteredRows : [])
        .map((row) => Number(row?.id || 0))
        .filter((value) => value > 0);
    },
    headerActions() {
      return [
        {
          key: 'edit',
          label: this.text.actions.edit,
          icon: 'fa-pencil',
          variant: 'secondary',
        },
        {
          key: 'export',
          label: this.text.actions.export,
          icon: 'fa-file-excel-o',
          variant: 'secondary',
          className: 'app-header-btn--export',
        },
        {
          key: 'sync-account',
          label: this.text.actions.sync,
          icon: 'fa-refresh',
          variant: 'secondary',
          className: 'app-header-btn--sync',
          iconOnly: true,
          title: this.text.confirm.sync.title,
        },
      ];
    },
    accountTitle() {
      const name = String(this.account?.name || '').trim();
      return name || this.text.page.defaultAccountTitle;
    },
    breadcrumbItems() {
      return [
        { label: String(this.text.page.title || '').trim(), to: { name: 'financial' } },
        { label: this.accountTitle },
      ];
    },
    accountReference() {
      const number = String(this.account?.masked_account_number || '').trim();
      return number || String(this.account?.type?.code || '').trim();
    },
    accountActive() {
      return Number(this.account?.is_active || 0) === 1;
    },
    accountStatusLabel() {
      return this.accountActive
        ? this.text.status.active
        : this.text.status.inactive;
    },
    confirmActionTitle() {
      if (this.pendingActionType === 'sync-account') {
        return this.text.confirm.sync.title;
      }

      if (this.pendingActionType === 'enable-account') {
        return this.text.confirm.enable.title;
      }

      if (this.pendingActionType === 'disable-account') {
        return this.text.confirm.disable.title;
      }

      return this.text.confirm.delete.title;
    },
    confirmActionSubtitle() {
      if (this.pendingActionType === 'sync-account') {
        return this.text.confirm.sync.subtitle;
      }

      if (this.pendingActionType === 'enable-account') {
        return this.text.confirm.enable.subtitle;
      }

      if (this.pendingActionType === 'disable-account') {
        return this.text.confirm.disable.subtitle;
      }

      return this.text.confirm.delete.subtitle;
    },
    confirmActionMessage() {
      const accountName = String(this.editAccountData?.name || this.account?.name || '').trim() || this.text.page.defaultAccountTitle;
      if (this.pendingActionType === 'sync-account') {
        return this.text.confirm.sync.message(accountName);
      }

      if (this.pendingActionType === 'enable-account') {
        return this.text.confirm.enable.message(accountName);
      }

      if (this.pendingActionType === 'disable-account') {
        return this.text.confirm.disable.message(accountName);
      }

      return this.text.confirm.delete.message(accountName);
    },
    confirmActionButton() {
      if (this.pendingActionType === 'sync-account') {
        return this.text.confirm.sync.confirm;
      }

      if (this.pendingActionType === 'enable-account') {
        return this.text.confirm.enable.confirm;
      }

      if (this.pendingActionType === 'disable-account') {
        return this.text.confirm.disable.confirm;
      }

      return this.text.confirm.delete.confirm;
    },
    confirmActionTone() {
      if (this.pendingActionType === 'sync-account') {
        return 'info';
      }

      if (this.pendingActionType === 'enable-account') {
        return 'success';
      }

      return this.pendingActionType === 'delete-account' ? 'danger' : 'warning';
    },
    categoryModel: {
      get() {
        const currentValue = String(this.category || 'all').trim() || 'all';
        return this.categoryOptions.find((option) => option.value === currentValue)
          || this.categoryOptions[0]
          || null;
      },
      set(value) {
        const normalized = String(value?.value || 'all').trim() || 'all';
        this.category = normalized;
        this.reloadMovements();
      },
    },
    movementTypeModel: {
      get() {
        const currentValue = String(this.movementType || 'all').trim() || 'all';
        return this.movementTypeOptions.find((option) => option.value === currentValue)
          || this.movementTypeOptions[0]
          || null;
      },
      set(value) {
        const normalized = String(value?.value || 'all').trim() || 'all';
        this.movementType = normalized;
      },
    },
    periodModel: {
      get() {
        const currentValue = String(this.period || '').trim();
        return this.periodOptions.find((option) => option.value === currentValue)
          || this.periodOptions[0]
          || null;
      },
      set(value) {
        const normalized = String(value?.value || '').trim();
        this.period = normalized;

        if (normalized === '') {
          return;
        }

        const range = buildMonthRangeFromValue(normalized);
        if (!Array.isArray(range)) {
          return;
        }

        this.applyingPeriodSelection = true;
        this.dateRangeValue = range;
        this.reloadMovements();
      },
    },
  },
  created() {
    const requestedTab = String(this.$route?.query?.tab || '').trim().toLowerCase();
    if (['summary', 'movements', 'history'].includes(requestedTab)) {
      this.activeTab = requestedTab;
    }

    this.reloadMovements();
  },
  watch: {
    '$route.query.tab'(value) {
      const requestedTab = String(value || '').trim().toLowerCase();
      if (['summary', 'movements', 'history'].includes(requestedTab)) {
        this.activeTab = requestedTab;
      }
    },
  },
  methods: {
    resolveFinancialAccountSyncStorageKey() {
      const tenantKey = String(window?.location?.hostname || '').trim() || 'global';
      return `financial.account.last_sync_balances_at.${tenantKey}.${this.financialAccountId}`;
    },
    readLastFinancialAccountSyncAt() {
      const raw = localStorage.getItem(this.resolveFinancialAccountSyncStorageKey());
      const parsed = Number(raw || 0);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    },
    writeLastFinancialAccountSyncAt(timestamp) {
      localStorage.setItem(this.resolveFinancialAccountSyncStorageKey(), String(Number(timestamp || 0)));
    },
    resolveFinancialAccountSyncRemainingMinutes() {
      const lastAt = this.readLastFinancialAccountSyncAt();
      if (lastAt <= 0) {
        return 0;
      }

      const elapsed = Date.now() - lastAt;
      const remaining = FINANCIAL_ACCOUNT_SYNC_COOLDOWN_MS - elapsed;
      if (remaining <= 0) {
        return 0;
      }

      return Math.max(1, Math.ceil(remaining / (60 * 1000)));
    },
    canRunFinancialAccountSyncNow() {
      return this.resolveFinancialAccountSyncRemainingMinutes() <= 0;
    },
    openFinancialAccountSyncConfirmModal() {
      if (this.isFinancialAccountSyncRunning || this.financialAccountId <= 0) {
        return;
      }

      if (!this.canRunFinancialAccountSyncNow()) {
        const minutes = this.resolveFinancialAccountSyncRemainingMinutes();
        this.$toast?.info(this.text.messages.syncCooldown(minutes));
        return;
      }

      this.openConfirmActionModal('sync-account');
    },
    resolveMonthMeta(row) {
      const value = row?.raw_date || row?.happened_at || row?.date;
      const parsed = parsePresentationDate(value);

      if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) {
        return {
          key: '',
          label: this.text.movements.table.monthFallback,
        };
      }

      const year = parsed.getFullYear();
      const month = parsed.getMonth();
      const key = `${year}-${String(month + 1).padStart(2, '0')}`;

      return {
        key,
        label: formatMonthYearLabel(parsed, { fallback: this.text.movements.table.monthFallback }),
      };
    },
    toggleFilters() {
      const nextState = resolveToolbarToggleState(this.showFilters);
      this.showFilters = nextState.showFilters;

      if (nextState.showDateInput !== null) {
        this.showDateInput = nextState.showDateInput;
      }
    },
    handleFiltersAfterLeave() {
      this.showDateInput = false;
    },
    onDateRangeChange(payload) {
      if (this.applyingPeriodSelection) {
        this.applyingPeriodSelection = false;
      } else {
        this.period = '';
      }

      if (payload?.source === 'manual' && !shouldEmitToolbarDateChange(payload, this.dateRangeValue)) {
        return;
      }
      this.reloadMovements();
    },
    onHeaderAction(action) {
      const key = String(action?.key || '').trim();
      if (key === 'edit') {
        this.openEditAccountModal();
        return;
      }

      if (key === 'sync-account') {
        this.openFinancialAccountSyncConfirmModal();
        return;
      }

      if (key === 'export') {
        this.openExportModal();
      }
    },
    openExportModal() {
      if (this.financialAccountId <= 0) {
        return;
      }

      this.isExportModalOpen = true;
    },
    closeExportModal() {
      this.isExportModalOpen = false;
    },
    async openEditAccountModal(sectionKey = 'general') {
      if (this.financialAccountId <= 0 || this.editAccountSaving) {
        return;
      }

      this.editAccountSectionKey = String(sectionKey || 'general').trim() || 'general';

      try {
        const response = await financialAccountsService.show(this.financialAccountId);
        this.editAccountData = extractPayload(response);
        this.isEditAccountModalOpen = true;
      } catch (error) {
        const message = error?.response?.data?.message || this.text.messages.loadError;
        this.$toast?.error(message);
      }
    },
    closeEditAccountModal(force = false) {
      if (this.editAccountSaving && !force) {
        return;
      }

      this.isEditAccountModalOpen = false;
      this.editAccountData = null;
      this.editAccountSectionKey = 'general';
    },
    async handleEditAccountSubmit(payload) {
      if (this.financialAccountId <= 0 || this.editAccountSaving) {
        return;
      }

      this.editAccountSaving = true;

      try {
        const response = await financialAccountsService.update(this.financialAccountId, payload || {});
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, this.text.messages.updateSuccess));
        this.closeEditAccountModal(true);
        await this.reloadMovements();
      } catch (error) {
        const message = error?.response?.data?.message || this.text.messages.updateError;
        this.$toast?.error(message);
      } finally {
        this.editAccountSaving = false;
      }
    },
    handleRequestDisableAccount() {
      this.openConfirmActionModal('disable-account');
    },
    handleRequestEnableAccount() {
      this.openConfirmActionModal('enable-account');
    },
    handleRequestDeleteAccount() {
      this.openConfirmActionModal('delete-account');
    },
    openConfirmActionModal(actionType) {
      this.pendingActionType = String(actionType || '').trim();
      this.isConfirmActionModalOpen = true;
    },
    closeConfirmActionModal() {
      if (this.pendingActionType === 'sync-account' && this.isFinancialAccountSyncRunning) {
        return;
      }

      if (this.pendingActionType !== 'sync-account' && this.confirmActionLoading) {
        return;
      }

      this.isConfirmActionModalOpen = false;
      this.pendingActionType = '';
    },
    async confirmPendingAction() {
      if (this.pendingActionType === 'sync-account') {
        await this.confirmFinancialAccountSyncAction();
        return;
      }

      if (this.financialAccountId <= 0 || this.confirmActionLoading) {
        this.closeConfirmActionModal();
        return;
      }

      this.confirmActionLoading = true;

      try {
        if (this.pendingActionType === 'enable-account') {
          const response = await financialAccountsService.toggleStatus(this.financialAccountId, { is_active: 1 });
          this.$toast?.success(response?.message || this.text.messages.enableSuccess);
          this.closeEditAccountModal();
          await this.reloadMovements();
        } else if (this.pendingActionType === 'disable-account') {
          const response = await financialAccountsService.toggleStatus(this.financialAccountId, { is_active: 0 });
          this.$toast?.success(response?.message || this.text.messages.disableSuccess);
          this.closeEditAccountModal();
          await this.reloadMovements();
        } else if (this.pendingActionType === 'delete-account') {
          const response = await financialAccountsService.delete(this.financialAccountId);
          this.$toast?.success(response?.message || this.text.messages.deleteSuccess);
          this.closeEditAccountModal();
          this.$router.push({ name: 'financial' });
        }
      } catch (error) {
        const fallback = this.pendingActionType === 'delete-account'
          ? this.text.messages.deleteError
          : this.pendingActionType === 'enable-account'
            ? this.text.messages.enableError
            : this.text.messages.disableError;
        const message = error?.response?.data?.message || fallback || this.text.messages.actionError;
        this.$toast?.error(message);
      } finally {
        this.confirmActionLoading = false;
        this.closeConfirmActionModal();
      }
    },
    async confirmFinancialAccountSyncAction() {
      if (this.isFinancialAccountSyncRunning) {
        return;
      }

      if (!this.canRunFinancialAccountSyncNow()) {
        const minutes = this.resolveFinancialAccountSyncRemainingMinutes();
        this.closeConfirmActionModal();
        this.$toast?.info(this.text.messages.syncCooldown(minutes));
        return;
      }

      this.isFinancialAccountSyncRunning = true;

      try {
        const response = await financialAccountsService.syncBalances(
          { financial_account_id: this.financialAccountId },
          { skipLoading: true }
        );
        this.writeLastFinancialAccountSyncAt(Date.now());
        this.$toast?.success(response?.message || this.text.messages.syncSuccess);
        await this.reloadMovements();
      } catch (error) {
        const message = error?.response?.data?.message || this.text.messages.syncError;
        this.$toast?.error(message);
      } finally {
        this.isFinancialAccountSyncRunning = false;
        this.closeConfirmActionModal();
      }
    },
    resolveCategoryClass(categoryKey) {
      const normalized = String(categoryKey || '').trim().toLowerCase();
      if (normalized === 'income') return 'financial-category-pill--income';
      if (normalized === 'expense') return 'financial-category-pill--expense';
      if (normalized === 'transfer') return 'financial-category-pill--transfer';
      if (normalized === 'replacement') return 'financial-category-pill--replacement';
      if (normalized === 'init') return 'financial-category-pill--init';
      if (normalized === 'otherincome') return 'financial-category-pill--otherincome';
      if (normalized === 'otherexpense') return 'financial-category-pill--otherexpense';
      return 'financial-category-pill--neutral';
    },
    resolveMovementKey(row) {
      const normalized = String(row?.category_key || row?.type_reference || '').trim().toLowerCase();
      if (normalized !== '' && normalized !== 'unknown') {
        return normalized;
      }

      return Number(row?.is_income || 0) === 1 ? 'income' : 'expense';
    },
    resolveMovementLabel(row) {
      const label = String(row?.category || '').trim();
      if (label !== '') {
        return label;
      }

      return Number(row?.is_income || 0) === 1 ? 'Ingreso' : 'Egreso';
    },
    resolveConceptSubtitleRoute(row) {
      const subtitleType = String(row?.concept_subtitle_type || '').trim().toLowerCase();
      const subtitleId = Number(row?.concept_subtitle_id || 0);

      if (subtitleId <= 0) {
        return null;
      }

      if (subtitleType === 'customer') {
        return {
          name: 'customerDetail',
          params: { customerId: String(subtitleId) },
        };
      }

      if (subtitleType === 'supplier') {
        return {
          name: 'supplierDetail',
          params: { supplierId: String(subtitleId) },
        };
      }

      return null;
    },
    showPaymentFolioHeader(row) {
      const typeReference = String(row?.type_reference || '').trim().toLowerCase();
      const paymentFolio = String(row?.concept_payment_folio || '').trim();
      const subtitle = String(row?.concept_subtitle || '').trim();

      return paymentFolio !== '' && subtitle !== '' && (typeReference === 'income' || typeReference === 'expense');
    },
    resolvePaymentFolioRoute(row) {
      const typeReference = String(row?.type_reference || '').trim().toLowerCase();
      const referenceId = Number(row?.reference || 0);
      if (!Number.isFinite(referenceId) || referenceId <= 0) {
        return null;
      }

      if (typeReference === 'income') {
        return {
          name: 'paymentReceiptDetail',
          params: { paymentReceiptId: String(referenceId) },
        };
      }

      if (typeReference === 'expense') {
        return {
          name: 'expenseReceiptDetail',
          params: { expenseReceiptId: String(referenceId) },
        };
      }

      return null;
    },
    resolveConceptDetailRoute(row) {
      const label = String(row?.concept_detail_label || '').trim().toLowerCase();
      const invoiceId = Number(row?.concept_detail_invoice_id || 0);
      if (label === 'factura:' && invoiceId > 0) {
        return {
          name: 'invoiceDetail',
          params: { invoiceId: String(invoiceId) },
        };
      }

      const typeReference = String(row?.type_reference || '').trim().toLowerCase();
      const referenceId = Number(row?.reference || 0);
      if (!Number.isFinite(referenceId) || referenceId <= 0) {
        return null;
      }

      if (typeReference === 'income') {
        return {
          name: 'paymentReceiptDetail',
          params: { paymentReceiptId: String(referenceId) },
        };
      }

      if (typeReference === 'expense') {
        return {
          name: 'expenseReceiptDetail',
          params: { expenseReceiptId: String(referenceId) },
        };
      }

      return null;
    },
    async reloadMovements() {
      if (this.financialAccountId <= 0) {
        this.rows = [];
        return;
      }

      try {
        const response = await financialAccountsService.movements(
          this.financialAccountId,
          {
            category: this.category,
            ...extractToolbarDateRange(this.dateRangeValue, {
              startKey: 'date_from',
              endKey: 'date_to',
            }),
          }
        );

        const payload = extractPayload(response);
        this.account = payload?.account || null;
        this.rows = Array.isArray(payload?.rows) ? payload.rows : [];
        this.rows = this.rows.map((row) => ({
          ...row,
          raw_date: row?.raw_date || row?.date_value || row?.date || '',
        }));

        const nextCounts = payload?.summary?.counts && typeof payload.summary.counts === 'object'
          ? payload.summary.counts
          : {};

        this.counts = {
          all: Number(nextCounts.all || 0),
          income: Number(nextCounts.income || 0),
          expense: Number(nextCounts.expense || 0),
          transfer: Number(nextCounts.transfer || 0),
          init: Number(nextCounts.init || 0),
          otherincome: Number(nextCounts.otherincome || 0),
          otherexpense: Number(nextCounts.otherexpense || 0),
        };
      } catch (error) {
        this.rows = [];
      }
    },
  },
};
</script>

<style scoped>
.financial-account-detail-header-left {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.financial-account-detail-content-wrap {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.financial-account-detail-heading-main {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.financial-account-detail-heading-title-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.financial-account-detail-heading-title {
  margin: 0;
}

.financial-account-detail-heading-meta {
  display: flex;
}

.financial-account-detail-heading-balance {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}

.financial-account-detail-heading-balance small {
  color: #64748b;
  font-size: 12px;
}

.financial-account-detail-heading-balance strong {
  color: #0f172a;
  font-size: 16px;
}

.financial-account-movements-tab {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.financial-account-toolbar__right {
  align-items: center;
}

.financial-toolbar-category-inline {
  min-width: 250px;
}

.financial-account-filters-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 900px) {
  .financial-account-filters-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.financial-category-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  border: 1px solid transparent;
}

.financial-category-pill--income {
  color: #14532d;
  background: #dcfce7;
  border-color: #86efac;
}

.financial-category-pill--expense {
  color: #9f1239;
  background: #ffe4e6;
  border-color: #fda4af;
}

.financial-category-pill--transfer {
  color: #1e3a8a;
  background: #dbeafe;
  border-color: #93c5fd;
}

.financial-category-pill--replacement {
  color: #92400e;
  background: #fef3c7;
  border-color: #fcd34d;
}

.financial-category-pill--init {
  color: #6b21a8;
  background: #f3e8ff;
  border-color: #d8b4fe;
}

.financial-category-pill--otherincome {
  color: #065f46;
  background: #ccfbf1;
  border-color: #99f6e4;
}

.financial-category-pill--otherexpense {
  color: #7c2d12;
  background: #ffedd5;
  border-color: #fdba74;
}

.financial-category-pill--neutral {
  color: #334155;
  background: #e2e8f0;
  border-color: #cbd5e1;
}

.financial-amount-positive {
  color: #15803d;
}

.financial-amount-negative {
  color: #b91c1c;
}

.financial-account-movements-total-row {
  background: #f8fafc;
}

.financial-account-movements-total-label {
  color: #334155;
}

.financial-account-movements-total-cell {
  border-top: 2px solid #cbd5e1;
}

.financial-account-month-divider-row td {
  border-top: 1px solid #dbeafe;
  background: #f8fbff;
}

.financial-account-month-divider-cell {
  padding: 8px 12px;
}

.financial-account-month-divider-cell span {
  display: inline-block;
  font-size: 12px;
  font-weight: 700;
  color: #1e3a8a;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.app-data-table thead th.financial-col-center {
  text-align: center !important;
}

.financial-account-concept-detail-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.financial-account-concept-detail-row .app-muted:first-child {
  flex: 1;
  min-width: 0;
  padding-left: 36px;
}

.financial-account-concept-detail-row .app-muted:last-child {
  margin-left: auto;
  text-align: right;
  white-space: nowrap;
  min-width: 120px;
}
</style>
